import { type FC } from "react"
import { Link } from "react-router"

import { type ThinBokaderoCourse } from "@forento/core/models/bokadero"
import { cn, tw } from "@forento/ui/utilities/tailwind"

import CourseStatus from "~/components/course-status"
import Layout from "~/components/layout"
import Loader from "~/components/loader"
import { query } from "~/utilities/trpc"

const CoursesPage: FC = () => {
	const { data: courses, error } = query.listCourses.useQuery()

	return (
		<Layout>
			<div className="mx-auto flex w-full max-w-6xl flex-col gap-8 py-10">
				<h1 className="text-3xl font-semibold text-slate-900">Kurser</h1>
				{error ? (
					<div className="flex flex-col items-center justify-center py-10">
						<span className="text-lg font-medium text-slate-600">Kunde inte ladda kurser</span>
					</div>
				) : courses === undefined ? (
					<Loader label="Laddar kurser..." />
				) : (
					<div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
						{courses.map(course => (
							<Course key={course.id} {...course} />
						))}
						<Link
							to="/courses/create"
							className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-slate-400 transition-colors hover:bg-slate-100"
						>
							<span className="mb-2 text-4xl">+</span>
							<span className="font-medium">Lägg till en ny kurs</span>
						</Link>
					</div>
				)}
			</div>
		</Layout>
	)
}

const courseContainerClasses = tw`group relative overflow-hidden rounded-xl border border-slate-200 bg-white p-6 shadow-sm`

const Course: FC<ThinBokaderoCourse> = ({ id, name, thumbnailUrl, shortDescription, status, pagesCount }) => (
	<Link
		to={`/courses/${id}`}
		className={cn(courseContainerClasses, "cursor-pointer transition hover:-translate-y-0.5 hover:shadow-lg")}
	>
		<CourseStatus status={status} className="absolute top-4 right-4 shadow" />
		<Thumbnail url={thumbnailUrl} name={name} />
		<h2 className="group-hover:text-primary text-xl font-semibold text-slate-900 transition-colors">{name}</h2>
		<p className="mt-3 text-base text-slate-600">{shortDescription}</p>
		<div className="mt-6 flex items-center justify-between">
			<span className="text-sm text-slate-400">
				{pagesCount} {pagesCount === 1 ? "sida" : "sidor"}
			</span>
			<div className="text-primary font-medium transition-colors">Visa mer</div>
		</div>
	</Link>
)

const Thumbnail: FC<{ url: string | null; name: string }> = ({ url, name }) => (
	<div className="mb-4 flex justify-center">
		{url ? (
			<div className="flex aspect-video w-full max-w-full items-center justify-center overflow-hidden rounded border border-slate-200 bg-slate-100">
				<img src={url} alt={`Thumbnail för ${name}`} className="h-full w-full object-cover" />
			</div>
		) : (
			<div className="flex aspect-video w-full max-w-full items-center justify-center rounded border border-slate-200 bg-slate-100 text-slate-300">
				<span className="text-4xl">📷</span>
			</div>
		)}
	</div>
)

export default CoursesPage
